import { useEffect, useState, useRef } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

export default function VerifyMagicLink() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // Where to land afterwards (set by Sign in with Google / Apple); only paths in this app
  const returnTo = searchParams.get('returnTo')
  const destination = returnTo?.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '/'
  const { verifyMagicLink, marketer } = useAuth()
  const [error, setError] = useState('')
  const [verifying, setVerifying] = useState(true)
  const verificationAttempted = useRef(false)

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setError('Invalid magic link - no token provided')
        setVerifying(false)
        return
      }

      try {
        await verifyMagicLink(token)
        // Navigation will happen via the useEffect that monitors marketer
      } catch (error) {
        if (error instanceof Error) {
          setError(error.message)
        } else {
          setError('Failed to verify magic link')
        }
        setVerifying(false)
      }
    }

    // Only attempt verification once
    if (!verificationAttempted.current && token) {
      verificationAttempted.current = true
      verify()
    }
  }, [token, verifyMagicLink])

  // Redirect when user is authenticated
  useEffect(() => {
    if (marketer) {
      navigate(destination, { replace: true })
    }
  }, [marketer, navigate, destination])

  return (
    <div className="flex min-h-screen items-center justify-center py-8 relative">
      <div className="fixed inset-0 -z-10 bg-[#F4F7EF]"></div>

      <div className="w-full max-w-md px-4">
        <Card className="rounded-[28px] border-0 bg-[#FFFFFF] py-4 shadow-[0_20px_60px_rgba(16,24,40,0.08)] sm:px-4">
          <CardHeader className="text-center pb-0">
            <div className="flex justify-center mb-4">
              <img
                src="https://adthlete.us-iad-1.linodeobjects.com/public/adthlete_logomark.png"
                alt="Athios Logo"
                className="w-10 h-10"
              />
            </div>
            <CardTitle className="text-2xl font-bold text-[#0F172A]">
              Verifying Magic Link
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {verifying && !error && (
              <div className="text-center">
                <div className="mb-4">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#C0FD72]"></div>
                </div>
                <p className="text-[#4B5563]">Please wait while we verify your magic link...</p>
              </div>
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-md">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            {error && (
              <Button
                onClick={() => navigate('/signin')}
                className="w-full bg-[#C0FD72] hover:bg-[#a8e45a] text-black font-medium"
              >
                Return to Sign In
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
